import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Clock, Ban, Mail, ArrowRight, X } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sales Reconciliation Inbox
 *
 * A dismissable banner that surfaces items requiring admin attention:
 *   - Stale pending-verification sales (> 7 days old)
 *   - Recently cancelled sales (last 30 days)
 *   - Unmatched emails from the most recent Directory CSV import
 *
 * Shows only when there's something to reconcile. Clicking "Review Sales"
 * jumps to the relevant admin tab.
 */
export default function ReconciliationInbox({ getAuthHeader, onJumpToTab }) {
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/reconciliation-summary`, {
          headers: getAuthHeader(),
        });
        if (!cancelled) setSummary(res.data);
      } catch (_) {
        // Silent — banner stays hidden if endpoint fails.
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeader]);

  if (dismissed || !summary) return null;

  const hasStale = (summary.pending_stale || 0) > 0;
  const hasCancelled = (summary.cancelled_recent || 0) > 0;
  const hasUnmatched = (summary.unmatched_csv_last_import || 0) > 0;

  // Nothing to show → don't render.
  if (!hasStale && !hasCancelled && !hasUnmatched) return null;

  // Severity: stale pending sales are the most actionable.
  const severity = hasStale ? 'urgent' : 'info';
  const accent =
    severity === 'urgent'
      ? 'border-amber-500/40 bg-amber-500/10'
      : 'border-blue-500/40 bg-blue-500/10';
  const iconColor = severity === 'urgent' ? 'text-amber-400' : 'text-blue-400';

  return (
    <div
      className={`wm-card border ${accent} rounded-lg p-4 mb-4`}
      data-testid="reconciliation-inbox-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${iconColor}`} />
        <div className="flex-1">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-base font-semibold text-foreground">
              Sales Reconciliation Inbox
            </h3>
            <button
              onClick={() => setDismissed(true)}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Dismiss banner"
              data-testid="reconciliation-dismiss-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2 text-sm">
            {hasStale && (
              <span
                className="flex items-center gap-1.5 text-amber-300"
                data-testid="reconciliation-stale-count"
              >
                <Clock className="w-4 h-4" />
                <strong>{summary.pending_stale}</strong>
                &nbsp;pending &gt; {summary.stale_days_threshold} days
                {summary.pending_stale_amount > 0 && (
                  <span className="text-muted-foreground">
                    &nbsp;(${summary.pending_stale_amount.toLocaleString()})
                  </span>
                )}
              </span>
            )}
            {hasCancelled && (
              <span
                className="flex items-center gap-1.5 text-rose-300"
                data-testid="reconciliation-cancelled-count"
              >
                <Ban className="w-4 h-4" />
                <strong>{summary.cancelled_recent}</strong>
                &nbsp;cancelled in last 30 days
              </span>
            )}
            {hasUnmatched && (
              <span
                className="flex items-center gap-1.5 text-blue-300"
                data-testid="reconciliation-unmatched-count"
              >
                <Mail className="w-4 h-4" />
                <strong>{summary.unmatched_csv_last_import}</strong>
                &nbsp;unmatched CSV email{summary.unmatched_csv_last_import === 1 ? '' : 's'}
              </span>
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {(hasStale || hasCancelled) && (
              <Button
                size="sm"
                variant="outline"
                className="border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                onClick={() => onJumpToTab && onJumpToTab('sales')}
                data-testid="reconciliation-review-sales-btn"
              >
                Review Sales <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
            {hasUnmatched && (
              <Button
                size="sm"
                variant="outline"
                className="border-blue-500/40 text-blue-200 hover:bg-blue-500/10"
                onClick={() => onJumpToTab && onJumpToTab('edirectory')}
                data-testid="reconciliation-review-edir-btn"
              >
                Review Last CSV Import <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
